import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { User } from 'src/app/models/user';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { SessionUser } from 'src/app/models/session';
import { FSInviteUpdateType } from 'src/app/models/session-share';
import * as moment from 'moment-timezone';

export enum TimeUnit {
  SECOND = 'second',
  SECONDS = 'seconds',
  MINUTE = 'minute',
  MINUTES = 'minutes',
  HOUR = 'hour',
  HOURS = 'hours',
  DAY = 'day',
  DAYS = 'days',
  MONTH = 'month',
  MONTHS = 'months',
  YEAR = 'year',
  YEARS = 'years',
}

export enum JoinedSpaceOrigin {
  SPACES_MANAGER = 'spaces_manager',
}
export interface JoinedSpaceOriginInfo {
  origin: JoinedSpaceOrigin;
  spaceId: string;
}
@Injectable({
  providedIn: 'root',
})
export class SpaceSharingService {
  readonly REINVITE_TIME_IN_MILLIS = 5 * 60000;
  private joinedSpaceOriginInfo: JoinedSpaceOriginInfo | undefined;

  private readonly timeAgoTranslationMap: { [key: string]: string } = {
    [TimeUnit.MINUTE]: this.translateService.instant('{{time}} minute ago'),
    [TimeUnit.MINUTES]: this.translateService.instant('{{time}} minutes ago'),
    [TimeUnit.HOUR]: this.translateService.instant('{{time}} hour ago'),
    [TimeUnit.HOURS]: this.translateService.instant('{{time}} hours ago'),
    [TimeUnit.DAY]: this.translateService.instant('{{time}} day ago'),
    [TimeUnit.DAYS]: this.translateService.instant('{{time}} days ago'),
    [TimeUnit.MONTH]: this.translateService.instant('{{time}} month ago'),
    [TimeUnit.MONTHS]: this.translateService.instant('{{time}} months ago'),
    [TimeUnit.YEAR]: this.translateService.instant('{{time}} year ago'),
    [TimeUnit.YEARS]: this.translateService.instant('{{time}} years ago'),
  };

  constructor(private telemetry: TelemetryService, private translateService: TranslateService) {}
  public splitOnSignUpStatus(
    users: SessionUser[],
    lookupTable: { [id: string]: User } | undefined = undefined,
  ): number[] {
    if (lookupTable === undefined) {
      return [0, 0];
    }
    return users.reduce(
      ([signedUpCount, newCount], user) =>
        lookupTable[user._id]?.name ? [signedUpCount + 1, newCount] : [signedUpCount, newCount + 1],
      [0, 0],
    );
  }

  public fireFSInviteCountEvent(
    signedUpCount: number,
    newCount: number,
    type: FSInviteUpdateType,
  ): void {
    if (signedUpCount === 0 && newCount === 0) {
      return;
    }
    const suffix = type === FSInviteUpdateType.SPACE_USERS_INVITED ? 'invited' : 'reinvited';
    this.telemetry.event(`[${type}]`, {
      [`n_users_${suffix}`]: signedUpCount,
      [`n_new_users_${suffix}`]: newCount,
    });
  }

  public buildLookupTable(profiles: any): any {
    const lookup: { [key: string]: User } = {};
    if (!profiles) {
      return lookup;
    }
    for (const user of profiles) {
      lookup[user._id] = user;
    }
    return lookup;
  }

  getTimeInLargestUnitSeparated(
    diffInMillis: number,
    fractionHandler = Math.floor,
  ): { time: number; unit: TimeUnit } {
    const duration = moment.duration(diffInMillis);
    if (duration.asSeconds() < 60) {
      const seconds = fractionHandler(duration.asSeconds());
      return { time: seconds, unit: seconds === 1 ? TimeUnit.SECOND : TimeUnit.SECONDS };
    } else if (duration.asMinutes() < 60) {
      const minutes = fractionHandler(duration.asMinutes());
      return { time: minutes, unit: minutes === 1 ? TimeUnit.MINUTE : TimeUnit.MINUTES };
    } else if (duration.asHours() < 24) {
      const hours = fractionHandler(duration.asHours());
      return { time: hours, unit: hours === 1 ? TimeUnit.HOUR : TimeUnit.HOURS };
    } else if (duration.asDays() < 30) {
      const days = fractionHandler(duration.asDays());
      return { time: days, unit: days === 1 ? TimeUnit.DAY : TimeUnit.DAYS };
    } else if (duration.asMonths() < 12) {
      const months = fractionHandler(duration.asMonths());
      return { time: months, unit: months === 1 ? TimeUnit.MONTH : TimeUnit.MONTHS };
    }
    const years = fractionHandler(duration.asYears());
    return { time: years, unit: years === 1 ? TimeUnit.YEAR : TimeUnit.YEARS };
  }

  getLocalizedTimeSince(diffInMillis: number, fractionHandler = Math.floor): string {
    if (diffInMillis <= 0) {
      return '';
    }

    const { time, unit } = this.getTimeInLargestUnitSeparated(diffInMillis, fractionHandler);

    if (unit === TimeUnit.SECOND || unit === TimeUnit.SECONDS) {
      return this.translateService.instant('Just now');
    }

    // Use the translation map with proper interpolation
    const translationKey = this.timeAgoTranslationMap[unit];
    return translationKey.replace('{{time}}', time.toString());
  }

  public fireFSEventAcceptInvite(isInvitedByEmail: boolean, spaceId: string) {
    const urlParams = new URLSearchParams(window.location.search);
    let inviteSource = 'link';
    let referralSource = 'link';
    if (isInvitedByEmail) {
      inviteSource = 'email';
      if (
        this.joinedSpaceOriginInfo?.origin === JoinedSpaceOrigin.SPACES_MANAGER &&
        this.joinedSpaceOriginInfo.spaceId === spaceId
      ) {
        referralSource = 'space_manager';
      } else if (urlParams.get('utm_source') === 'email') {
        referralSource = 'email';
      }
    }
    this.telemetry.event('[user_accepted_invite]', {
      invite_source: inviteSource,
      referral_source: referralSource,
    });
  }
  public setJoinedSpaceOriginInfo(origin: JoinedSpaceOrigin, spaceId: string) {
    this.joinedSpaceOriginInfo = { origin, spaceId };
  }
}
